import { StandaloneComponentProps } from '@/types/component';
import { getBrandBy } from '@/utils/getBrandBy';
import { getSite } from '@/utils/getSite';
import NextImage, { ImageProps as NextImageProps } from 'next/image';
import { useState } from 'react';
import { useEffectOnce } from 'react-use';
import { VIDEO_PLAYED_TYPE } from './JWPlayerContext';
import { VideoArticle, VideoArticleProps } from './VideoArticle';
import { VideoArticleContainerProps } from './VideoArticle.Container';
import { VideoArticleExitProps } from './VideoArticle.Exit';
import { VideoArticleExitLinkProps } from './VideoArticle.Exit.Link';
import { VideoArticleExitMarketingTextProps } from './VideoArticle.Exit.MarketingText';
import { VideoArticleHeaderProps } from './VideoArticle.Header';
import { VideoArticleHeaderLinkProps } from './VideoArticle.Header.Link';
import { VideoArticleHeaderTitleProps } from './VideoArticle.Header.Title';
import { VideoArticleUnmuteProps } from './VideoArticle.Unmute';
import { VideoArticleUnmuteLabelProps } from './VideoArticle.Unmute.Label';
import { VideoArticleVideoContainerProps } from './VideoArticle.VideoContainer';
import { VideoArticleVideoContainerDurationProps } from './VideoArticle.VideoContainer.Duration';
import { VideoArticleVideoContainerNextVideoProps } from './VideoArticle.VideoContainer.NextVideo';
import { VideoArticleVideoContainerNextVideoDurationProps } from './VideoArticle.VideoContainer.NextVideo.Duration';
import { VideoArticleVideoContainerNextVideoTitleProps } from './VideoArticle.VideoContainer.NextVideo.Title';
import { VideoArticleDescriptionProps } from './VideoArticle.VideoDescription';
import { B2B_USER_INFO, VERTICAL_ASPECT_RATIO } from './constants';
import { B2BVideoInfo, LabradorFieldsTextSize, VideoB2BCampaign, VideoMetaData, VideoPlayerType } from './types/video';
import { getVideoCampaigns } from './utils/getVideoCampaigns';

export interface StandaloneJwVideoArticleProps extends StandaloneComponentProps {
  playerSrc?: string;
  hideNextVideoPopup?: boolean;
  videoData?: VideoPlayerType;
  videoPageLink?: string;
  durationMinutes?: string;
  disableLazyLoad?: boolean;
  showDescription?: boolean;
  titleTextSize?: LabradorFieldsTextSize;
  metadata?: VideoMetaData;
  verticalFloatingMode?: boolean;
  b2bVideo?: B2BVideoInfo;
  onComplete?: () => void;
  options?: VideoArticleProps & {
    $container?: VideoArticleContainerProps;
    $header?: VideoArticleHeaderProps & {
      $headerTitle?: VideoArticleHeaderTitleProps;
      $headerLink?: VideoArticleHeaderLinkProps;
    };
    $image?: Partial<NextImageProps>;
    $videoContainer?: VideoArticleVideoContainerProps & {
      $thumbnail?: Partial<NextImageProps>;
    };
    $duration?: VideoArticleVideoContainerDurationProps;
    $nextVideo?: VideoArticleVideoContainerNextVideoProps;
    $nextVideoDuration?: VideoArticleVideoContainerNextVideoDurationProps;
    $nextVideoTitle?: VideoArticleVideoContainerNextVideoTitleProps;
    $unmute?: VideoArticleUnmuteProps & {
      $label?: VideoArticleUnmuteLabelProps;
    };
    $exit?: VideoArticleExitProps & {
      $link?: VideoArticleExitLinkProps;
      $label?: VideoArticleExitMarketingTextProps;
    };
    $description?: VideoArticleDescriptionProps;
  };
}

export const StandaloneJwVideoArticle = (props: StandaloneJwVideoArticleProps) => {
  const {
    hideNextVideoPopup,
    playerSrc,
    showDescription,
    videoPageLink,
    durationMinutes,
    titleTextSize,
    metadata,
    disableLazyLoad,
    videoData,
    verticalFloatingMode,
    b2bVideo,
    options,
  } = props;

  const {
    variant,
    colors,
    size,
    $container: containerProps,
    $header: headerProps,
    $videoContainer: videoContainerProps,
    $duration: durationProps,
    $nextVideo: nextVideoProps,
    $nextVideoDuration: nextVideoDurationProps,
    $nextVideoTitle: nextVideoTitleProps,
    $unmute: unmuteProps,
    $image: imageProps,
    $exit: exitProps,
    $description: descriptionProps,
    ...baseProps
  } = options ?? {};
  const { $label: unmuteLabelProps } = unmuteProps ?? {};
  const { $headerTitle: headerTitleProps, $headerLink: headerLinkProps } = headerProps ?? {};
  const { $link: exitLinkProps, $label: exitLabelProps } = exitProps ?? {};
  const { $thumbnail: videoContainerThumbnailProps, ...otherVideoContainerProps } = videoContainerProps ?? {};

  const siteId = getSite();
  const { slug } = getBrandBy({ slug: siteId }) ?? {};
  const [showNextVideoPopup, setShowNextVideoPopup] = useState(!hideNextVideoPopup);
  const [videoCampaigns, setVideoCampaigns] = useState<VideoB2BCampaign[]>([]);
  const { NATIVE, RECOMMENDED } = VIDEO_PLAYED_TYPE;
  const [videoPlacementPriority, setVideoPlacementPriority] = useState<string[]>([RECOMMENDED]);

  const hasAllCampaignsBeenPlayed = (campaigns: VideoB2BCampaign[]) => {
    const b2bVideoInfo = localStorage.getItem(B2B_USER_INFO);
    const b2bVideoInfoParsed = b2bVideoInfo ? JSON.parse(b2bVideoInfo) : {};
    const { seenCampaigns } = b2bVideoInfoParsed;
    if (!seenCampaigns) return false;
    return campaigns.every((campaign) => {
      const frequency = seenCampaigns[campaign.campaignId];
      return frequency && frequency >= campaign.userFrequency;
    });
  };

  const fetchVideoCampaigns = async () => {
    const { playlist } = videoData ?? {};
    const { brand, video_category1, video_category2 } = playlist ?? {};
    const vertical = video_category1 || video_category2;
    if (brand && vertical) {
      const videoCampaigns = await getVideoCampaigns(vertical, brand);
      setVideoCampaigns(videoCampaigns);
      videoCampaigns.length && !hasAllCampaignsBeenPlayed(videoCampaigns)
        ? setVideoPlacementPriority([NATIVE, RECOMMENDED])
        : setVideoPlacementPriority([RECOMMENDED]);
    }
  };

  useEffectOnce(() => {
    fetchVideoCampaigns();
  });

  const isAutoplay = videoData?.autostart === 'viewable';
  const orientation = videoData?.aspectRatio === VERTICAL_ASPECT_RATIO ? 'vertical' : 'horizontal';

  return (
    <VideoArticle playerSrc={playerSrc} size={size ?? orientation} {...{ variant, colors }} {...baseProps}>
      <VideoArticle.Container
        verticalFloatingMode={verticalFloatingMode}
        showNextVideoPopup={showNextVideoPopup}
        setShowNextVideoPopup={setShowNextVideoPopup}
        {...containerProps}
      >
        <VideoArticle.MetaData {...metadata} />
        {videoData?.title && (
          <VideoArticle.Header titleTextSize={titleTextSize} {...headerProps}>
            <VideoArticle.Header.Title mediaId={videoData?.videoId} {...headerTitleProps}>
              {videoData?.title}
            </VideoArticle.Header.Title>

            <VideoArticle.Header.Link videoPageLink={videoPageLink} mediaId={videoData?.videoId} {...headerLinkProps}>
              <NextImage fill src={`/image/${slug}-video-logo.svg`} alt="Brand logo" {...imageProps} />
            </VideoArticle.Header.Link>
          </VideoArticle.Header>
        )}
        <VideoArticle.VideoContainer
          mediaId={videoData?.videoId}
          thumbnail={{ ...videoContainerThumbnailProps, src: videoData?.thumbnail ?? '' }}
          title={videoData?.title}
          variant={orientation}
          {...otherVideoContainerProps}
        >
          {!isAutoplay && videoData?.videoId && durationMinutes && (
            <VideoArticle.VideoContainer.Duration mediaId={videoData?.videoId} {...durationProps}>
              {durationMinutes}
            </VideoArticle.VideoContainer.Duration>
          )}
          {/* Without a div wrapper here, playing next video sometimes causes this error:
           `NotFoundError: Failed to execute 'insertBefore' on 'Node': The node before which the new node is to be inserted is not a child of this node.`
           Probably due to JW manipulating the DOM at the same time as React's reconciliation process.
           */}
          <div>
            {showNextVideoPopup && !isAutoplay && (
              <VideoArticle.VideoContainer.NextVideo {...nextVideoProps}>
                <VideoArticle.VideoContainer.NextVideo.Title mediaId={videoData?.videoId} {...nextVideoTitleProps}>
                  {videoData?.title}
                </VideoArticle.VideoContainer.NextVideo.Title>
                <VideoArticle.VideoContainer.NextVideo.Duration
                  mediaId={videoData?.videoId}
                  {...nextVideoDurationProps}
                >
                  {durationMinutes}
                </VideoArticle.VideoContainer.NextVideo.Duration>
              </VideoArticle.VideoContainer.NextVideo>
            )}
          </div>
          {videoData && (
            <VideoArticle.JWPlayer
              {...videoData}
              verticalFloatingMode={verticalFloatingMode}
              disableLazyLoad={disableLazyLoad}
              videoPlacementPriority={videoPlacementPriority}
            />
          )}
          <VideoArticle.Unmute {...unmuteProps}>
            <VideoArticle.Unmute.Label {...unmuteLabelProps}>Slå på ljud</VideoArticle.Unmute.Label>
          </VideoArticle.Unmute>
          <VideoArticle.Exit
            hideNextVideoPopup={hideNextVideoPopup}
            setShowNextVideoPopup={setShowNextVideoPopup}
            mediaId={videoData?.videoId}
            labelProps={exitLabelProps}
            linkProps={exitLinkProps}
            b2bVideo={b2bVideo}
            videoCampaigns={videoCampaigns}
            {...exitProps}
          />
        </VideoArticle.VideoContainer>

        {showDescription && (
          <VideoArticle.Description mediaId={videoData?.videoId} {...descriptionProps}>
            {videoData?.description}
          </VideoArticle.Description>
        )}
      </VideoArticle.Container>
    </VideoArticle>
  );
};
